type BrowserPrintSendCallback = (response?: unknown) => void
type BrowserPrintErrorCallback = (error: unknown) => void

export interface BrowserPrintDevice {
  uid: string
  name: string
  send: (
    data: string,
    onSuccess?: BrowserPrintSendCallback,
    onError?: BrowserPrintErrorCallback
  ) => void
}

interface BrowserPrintModule {
  getDefaultDevice: (
    deviceType: 'printer',
    onSuccess: (device: BrowserPrintDevice) => void,
    onError: BrowserPrintErrorCallback
  ) => void
  getLocalDevices: (
    onSuccess: (devices: BrowserPrintDevice[] | { printer?: BrowserPrintDevice[] }) => void,
    onError: BrowserPrintErrorCallback,
    deviceType?: 'printer'
  ) => void
}

declare global {
  interface Window {
    BrowserPrint?: BrowserPrintModule
  }
}

const BROWSER_PRINT_SCRIPT_URL = '/zebra/BrowserPrint-3.1.250.min.js'
let browserPrintLoadPromise: Promise<void> | null = null

const asErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'string' && error.trim()) return error
  if (error instanceof Error && error.message.trim()) return error.message
  return fallback
}

const normalizeDeviceList = (
  devices: BrowserPrintDevice[] | { printer?: BrowserPrintDevice[] } | unknown
): BrowserPrintDevice[] => {
  if (Array.isArray(devices)) return devices
  if (devices && typeof devices === 'object' && Array.isArray((devices as any).printer)) {
    return (devices as any).printer
  }
  return []
}

export async function ensureBrowserPrintLoaded(): Promise<void> {
  if (typeof window === 'undefined') {
    throw new Error('BrowserPrint is only available in a browser environment.')
  }

  if (window.BrowserPrint) return

  if (!browserPrintLoadPromise) {
    browserPrintLoadPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        `script[data-browser-print-script="true"]`
      )

      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true })
        existing.addEventListener(
          'error',
          () => reject(new Error('Failed to load Zebra BrowserPrint script.')),
          { once: true }
        )
        return
      }

      const script = document.createElement('script')
      script.src = BROWSER_PRINT_SCRIPT_URL
      script.async = true
      script.dataset.browserPrintScript = 'true'
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load Zebra BrowserPrint script.'))
      document.body.appendChild(script)
    })
  }

  await browserPrintLoadPromise

  if (!window.BrowserPrint) {
    throw new Error('Zebra BrowserPrint is not available.')
  }
}

export async function listLocalPrinters(): Promise<BrowserPrintDevice[]> {
  await ensureBrowserPrintLoaded()
  const browserPrint = window.BrowserPrint
  if (!browserPrint) throw new Error('Zebra BrowserPrint is not available.')

  return new Promise((resolve, reject) => {
    browserPrint.getLocalDevices(
      (devices) => {
        const normalized = normalizeDeviceList(devices)
        resolve(normalized)
      },
      (error) => reject(new Error(asErrorMessage(error, 'Error getting local printers.'))),
      'printer'
    )
  })
}

export async function getDefaultPrinter(): Promise<BrowserPrintDevice | null> {
  await ensureBrowserPrintLoaded()
  const browserPrint = window.BrowserPrint
  if (!browserPrint) throw new Error('Zebra BrowserPrint is not available.')

  return new Promise((resolve) => {
    browserPrint.getDefaultDevice(
      'printer',
      (device) => resolve(device || null),
      () => resolve(null)
    )
  })
}

export async function sendToPrinter(printerUid: string, data: string): Promise<void> {
  if (!printerUid) throw new Error('Printer is required.')

  const printers = await listLocalPrinters()
  const printer = printers.find((device) => device.uid === printerUid)
  if (!printer) throw new Error('Selected printer not found.')

  await new Promise<void>((resolve, reject) => {
    printer.send(
      data,
      () => resolve(),
      (error) => reject(new Error(asErrorMessage(error, 'Failed to send data to printer.')))
    )
  })
}

export async function getLocalPrinterList(
  setPrinterList: (options: Array<{ label: string; value: string }>) => void,
  onError?: (error: unknown) => void
) {
  try {
    const printers = await listLocalPrinters()
    setPrinterList(
      printers.map((printer) => ({
        label: printer.name,
        value: printer.uid,
      }))
    )
  } catch (error) {
    onError?.(error)
  }
}

export async function writeToSelectedPrinter(
  selectedPrinter: string | null,
  data: string,
  onSuccess?: () => void,
  onError?: (error: unknown) => void
) {
  if (!selectedPrinter) {
    onError?.('Printer is not selected.')
    return
  }

  try {
    await sendToPrinter(selectedPrinter, data)
    onSuccess?.()
  } catch (error) {
    onError?.(error)
  }
}

